/**
 * Asset Type Configuration
 * Centralized configuration for asset type labels and colors
 */

export type AssetType = 'crypto' | 'stock' | 'index' | 'forex' | 'commodity' | 'bond' | 'futures' | 'etf' | 'fund';

export interface AssetTypeConfig {
  label: string;
  labelZh: string;
  color: string; // Tailwind classes for light/dark mode
}

/**
 * Asset type configuration with colors matching portfolio page style
 * Using light background colors with dark text for better readability
 */
export const assetTypeConfig: Record<AssetType, AssetTypeConfig> = {
  crypto: {
    label: 'Crypto',
    labelZh: '加密货币',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
  },
  stock: {
    label: 'Stock',
    labelZh: '股票',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  },
  index: {
    label: 'Index',
    labelZh: '指数',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400',
  },
  forex: {
    label: 'Forex',
    labelZh: '外汇',
    color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/20 dark:text-violet-400',
  },
  commodity: {
    label: 'Commodity',
    labelZh: '商品',
    color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-400',
  },
  futures: {
    label: 'Futures',
    labelZh: '期货',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
  },
  etf: {
    label: 'ETF',
    labelZh: 'ETF',
    color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/20 dark:text-teal-400',
  },
  fund: {
    label: 'Fund',
    labelZh: '基金',
    color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400',
  },
  bond: {
    label: 'Bond',
    labelZh: '债券',
    color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/20 dark:text-slate-400',
  },
};

/**
 * Get asset type configuration
 */
export const getAssetTypeConfig = (type: string): AssetTypeConfig => {
  return assetTypeConfig[type as AssetType] || {
    label: 'Other',
    labelZh: '其他',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
  };
};

/**
 * Get asset type label based on current language
 */
export const getAssetTypeLabel = (type: string, language: 'en' | 'zh' = 'en'): string => {
  const config = getAssetTypeConfig(type);
  return language === 'zh' ? config.labelZh : config.label;
};

/**
 * Get asset type color classes
 */
export const getAssetTypeColor = (type: string): string => {
  return getAssetTypeConfig(type).color;
};
